import { DatePicker, Icon, Input, Select, Switch } from "antd";
import { css } from "glamor";
import * as R from "ramda";
import React, { useCallback, useMemo, useState } from "react";
import { Mutation, Query } from "react-apollo";

import { formatDatetimeString } from "@arachnys/flow-app/src/formatters";
import RiskLevel from "@arachnys/styleguide/RiskLevel";
import Categories from "../../component/Categories";
import Country from "../../component/Country";
import SelectOptions from "../../component/SelectOptions";
import { EntityNameTooltip } from "../../container/EntityNameTooltip";
import TaskTitle from "../../container/TaskTitle";
import { countryOptions } from "../../data";
import {
    debounce,
    getCountrySource,
    getValueFromPath,
    linesOfBusinessAccessor,
    withApolloState,
} from "../../util";
import {
    ASSIGN_MUTATION,
    GET_LOBS,
    SEARCH_ASSIGNABLE_USERS,
    SEARCH_USERS,
} from "./queries";

const styles = {
    categoriesCell: css({
        whiteSpace: "inherit !important",
    }),
    ellipsisRow: css({
        width: "100%",
        overflow: "hidden",
        whiteSpace: "nowrap",
        textOverflow: "ellipsis",
    }),
    riskLevelCell: css({
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        width: "100%",
        height: "100%",
    }),
    countryHeader: css({
        width: "145px",
        fontWeight: "normal",
    }),
    userSelect: css({
        width: 185,
        fontWeight: "normal",
    }),
    assignSelect: css({
        width: "100%",
    }),
    startDatePicker: css({
        marginBottom: 5,
    }),
    lobsCell: css({
        wordBreak: "break-word",
        whiteSpace: "normal !important",
    }),
    lobsHeaderContainer: css({
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
    }),
    lobsSwitcher: css({
        marginTop: 3,
        textAlign: "center",
    }),
};

const riskLevelOptions = {
    Unknown: 0,
    Low: 1,
    Medium: 2,
    High: 3,
};

const RiskLevelField = ({ level }) => (
    <div className={styles.riskLevelCell}>
        <RiskLevel level={riskLevelOptions[level]} />
    </div>
);

const nameForEmptyResponse = "name_for_empty_response";
const notAssignedUser = {
    id: "0",
    username: "Not Assigned",
};

class FilterCountryTitle extends React.Component {
    state = {
        value: undefined,
    };

    handleChange = e => {
        this.setState({ value: e });
        this.props.filter({ [this.props.id]: e });
    };

    render() {
        if (!this.props.filter) {
            return this.props.title;
        }

        return (
            <SelectOptions
                className={`${styles.countryHeader}`}
                placeholder={this.props.placeholder}
                options={countryOptions}
                onChange={this.handleChange}
                value={this.state.value}
                allowClear
                dropdownMatchSelectWidth={false}
                size="small"
            />
        );
    }
}

const LobsNamesFilter = ({ filter, id, linesOfBusiness, placeholder }) => {
    const [selectedLobs, setSelectedLobs] = useState([]);
    const handleChange = useCallback(
        value => {
            setSelectedLobs(value);
            filter({
                [id]: value,
            });
        },
        [filter, id]
    );

    const lobsOptions = useMemo(
        () =>
            linesOfBusiness.map(lob => ({
                value: lob,
                label: lob,
            })),
        [linesOfBusiness]
    );

    return (
        <SelectOptions
            className={`${styles.countryHeader}`}
            placeholder={placeholder}
            options={lobsOptions}
            onChange={handleChange}
            value={selectedLobs}
            mode="multiple"
            dropdownMatchSelectWidth={false}
            size="small"
        />
    );
};
const LobsOperatorFilter = ({ filter }) => {
    const [orState, setOrState] = useState(true);

    const handleChange = useCallback(
        value => {
            setOrState(value);

            filter({
                linesOfBusinessSearchOperator: value ? "or" : "and",
            });
        },
        [filter]
    );

    return (
        <div className={styles.lobsSwitcher}>
            <Switch
                checkedChildren="OR"
                unCheckedChildren="AND"
                onClick={handleChange}
                checked={orState}
            />
        </div>
    );
};

const FilterLobsTitle = ({ filter, placeholder, id }) => (
    <Query query={GET_LOBS} cache-policy="cache-and-network">
        {withApolloState(({ data }) => {
            return (
                <div className={styles.lobsHeaderContainer}>
                    <LobsNamesFilter
                        filter={filter}
                        id={id}
                        linesOfBusiness={data.linesOfBusiness}
                        placeholder={placeholder}
                    />
                    {/* AR-11281 removed launch darkly "lines-of-business-and-or-switcher" */}
                </div>
            );
        })}
    </Query>
);

const FilterTitle = ({ title, filter, placeholder, id, onClick }) => {
    return filter ? (
        <FilterTitleField
            onClick={onClick}
            placeholder={placeholder}
            filter={filter}
            id={id}
        />
    ) : (
        title
    );
};

const FilterTitleField = ({ placeholder, filter, id, onClick }) => {
    const handleChange = e => {
        filter({ [id]: e.target.value });
    };

    return (
        <Input
            suffix={<Icon type="search" />}
            placeholder={placeholder}
            onChange={handleChange}
            onClick={onClick}
            size="small"
        />
    );
};

class AssignSelectQuery extends React.Component {
    constructor(props) {
        super(props);

        this.refetch = () => {};

        this.state = {
            value: "",
            users: [],
            initial: true,
            // hack. As long as antd default behaviour is to show
            // previously selected value as a placeholder,
            // we need to set its `value` prop to undefined,
            // in order to be able `placeholder` prop to take power.
            // See T8357
            shouldIgnoreCurrentOwnerId: false,
        };
    }

    handleChange = id => {
        this.props.assignUser(id);
    };

    searchUsers = debounce(
        async () => {
            const results = await this.refetch({
                name: this.state.value || nameForEmptyResponse,
            });
            this.setState({
                users: R.pathOr(
                    [],
                    ["data", "task", "assignableUsers"],
                    results
                ),
            });
        },
        300,
        false
    );

    handleSearch = value => {
        this.setState(
            {
                value,
                initial: false,
            },
            this.searchUsers
        );
    };

    handleOnDropdownVisibleChange = isOpen => {
        this.setState({ shouldIgnoreCurrentOwnerId: !!isOpen });
    };

    render() {
        const { owner, taskId } = this.props;
        return (
            <Query
                query={SEARCH_ASSIGNABLE_USERS}
                variables={{
                    taskId: btoa(`Task:${taskId}`),
                    name: nameForEmptyResponse,
                }}
                cache-policy="cache-and-network"
                // Preventing a graphql query on component load
                skip={!this.state.value}
            >
                {apolloProps => {
                    this.refetch = apolloProps.refetch;

                    const noOwner = R.equals(owner, notAssignedUser);
                    const users = [
                        notAssignedUser,
                        ...(!noOwner && this.state.initial ? [owner] : []),
                        ...(this.state.initial ? [] : this.state.users),
                    ];

                    return (
                        <Select
                            showSearch
                            value={
                                this.state.shouldIgnoreCurrentOwnerId
                                    ? undefined
                                    : owner.id
                            }
                            size="small"
                            defaultActiveFirstOption={false}
                            showArrow={false}
                            filterOption={false}
                            onSearch={this.handleSearch}
                            onChange={this.handleChange}
                            notFoundContent={null}
                            className={`${styles.assignSelect}`}
                            onDropdownVisibleChange={
                                this.handleOnDropdownVisibleChange
                            }
                            placeholder="Start typing username"
                        >
                            {users.map(user => (
                                <Select.Option value={user.id} key={user.id}>
                                    {user.username}
                                </Select.Option>
                            ))}
                        </Select>
                    );
                }}
            </Query>
        );
    }
}

const AssignSelectMutation = ({ taskId, owner }) => (
    <Mutation mutation={ASSIGN_MUTATION}>
        {withApolloState((apolloProps, assignUser) => {
            const assign = userId =>
                assignUser({
                    variables: {
                        taskId: +taskId,
                        userId: +userId,
                    },
                });
            return (
                <AssignSelectQuery
                    owner={owner}
                    taskId={taskId}
                    assignUser={assign}
                />
            );
        })}
    </Mutation>
);

const assignSelect = permissionsData => row => {
    const canAssign = permissionsData.permissions.task.canAssign;

    const owner = R.pathOr(notAssignedUser, ["original", "node", "owner"], row);
    const taskId = R.pathOr(0, ["original", "node", "uid"], row);

    if (!canAssign) {
        return owner.username;
    } else {
        return <AssignSelectMutation taskId={taskId} owner={owner} />;
    }
};

class UsersFilter extends React.Component {
    constructor(props) {
        super(props);

        this.refetch = () => {};

        this.state = {
            value: "",
            users: [],
            initial: true,
        };
    }

    searchUsers = debounce(async () => {
        const results = await this.refetch({
            name: this.state.value || nameForEmptyResponse,
        });
        this.setState({
            users: R.pathOr([], ["data", "users"], results),
        });
    }, 300);

    handleSearch = value => {
        this.setState(
            {
                value,
                initial: false,
            },
            this.searchUsers
        );
    };

    handleChange = selectedUsers => {
        this.props.filter({ ownerIds: selectedUsers });
    };

    render() {
        const { users } = this.state;
        return (
            <Query
                query={SEARCH_USERS}
                variables={{
                    name: nameForEmptyResponse,
                }}
                cache-policy="cache-and-network"
            >
                {apolloProps => {
                    this.refetch = apolloProps.refetch;

                    return (
                        <Select
                            showSearch
                            placeholder="Owners"
                            mode="multiple"
                            defaultActiveFirstOption={false}
                            filterOption={false}
                            onSearch={this.handleSearch}
                            onChange={this.handleChange}
                            notFoundContent={null}
                            className={`${styles.userSelect}`}
                            size="small"
                        >
                            {users.map(user => (
                                <Select.Option
                                    value={user.id}
                                    key={user.id}
                                    title={user.username}
                                >
                                    {user.username}
                                </Select.Option>
                            ))}
                        </Select>
                    );
                }}
            </Query>
        );
    }
}

const DatesRange = ({ onChange }) => {
    const [startValue, setStartValue] = useState(null);
    const [endValue, setEndValue] = useState(null);
    const [endOpen, setEndOpen] = useState(false);

    const disabledStartDate = useCallback(
        startValue => {
            if (!startValue || !endValue) {
                return false;
            }
            return startValue.valueOf() > endValue.valueOf();
        },
        [endValue]
    );

    const disabledEndDate = useCallback(
        endValue => {
            if (!endValue || !startValue) {
                return false;
            }
            return endValue.valueOf() <= startValue.valueOf();
        },
        [startValue]
    );

    const onStartChange = useCallback(
        value => {
            setStartValue(value);
            // case when start value is removed using [x] button
            if (endValue && !value) {
                onChange([value, endValue]);
            }
        },
        [endValue, onChange]
    );

    const onEndChange = useCallback(
        value => {
            setEndValue(value);
            onChange([startValue, value]);
        },
        [startValue, onChange]
    );

    const handleStartOpenChange = open => {
        if (!open) {
            setEndOpen(true);
        }
    };

    const handleEndOpenChange = open => {
        setEndOpen(open);
    };

    return (
        <>
            <DatePicker
                disabledDate={disabledStartDate}
                format="YYYY-MM-DD"
                value={startValue}
                placeholder="Start Date"
                onChange={onStartChange}
                onOpenChange={handleStartOpenChange}
                size="small"
                className={`${styles.startDatePicker}`}
            />
            <br />
            <DatePicker
                disabledDate={disabledEndDate}
                format="YYYY-MM-DD"
                value={endValue}
                placeholder="End Date"
                onChange={onEndChange}
                open={endOpen}
                onOpenChange={handleEndOpenChange}
                size="small"
            />
        </>
    );
};

export const getTaskColumns = (
    baseAccessor = "node",
    filter,
    nameRenderer,
    permissionsData,
    preventSort
) => {
    const onDatesChange = momentDates => {
        const dates = momentDates.map(d => (d ? d.format("YYYY-MM-DD") : null));

        filter({
            createdAfter: dates[0],
            createdBefore: dates[1],
        });
    };

    return [
        {
            Header: (
                <FilterTitle
                    title="Name"
                    filter={filter}
                    placeholder="Entity"
                    id="entityName"
                />
            ),
            id: "entityName",
            accessor: `${baseAccessor}.entity.name`,
            Cell: EntityNameTooltip(true),
            filterable: false,
            sortable: false,
            minWidth: 100,
        },
        {
            Header: "Risk Level",
            id: "entityRiskLevel",
            accessor: `${baseAccessor}.entity.riskLevel`,
            Cell: row => <RiskLevelField level={row.value} />,
            maxWidth: 80,
            sortable: false,
        },
        {
            Header: (
                <FilterCountryTitle
                    title="Country"
                    filter={filter}
                    placeholder="Country"
                    id="countryCode"
                />
            ),
            id: "entityCountry",
            accessor: row => {
                const entity = getValueFromPath(`${baseAccessor}.entity`, row);
                const countrySource = getCountrySource(entity);

                return getValueFromPath(
                    `${countrySource}Address.countryCode`,
                    entity
                );
            },
            filterable: false,
            sortable: false,
            Cell: row => <Country.Name>{row.value}</Country.Name>,
            width: 160,
        },
        {
            Header: (
                <FilterLobsTitle
                    title="Lines of Business"
                    filter={filter}
                    placeholder="Lines of Business"
                    id="linesOfBusiness"
                />
            ),
            className: `${styles.lobsCell}`,
            id: "linesOfBusiness",
            accessor: linesOfBusinessAccessor(baseAccessor + ".entity"),
            filterable: false,
            sortable: false,
            width: 160,
        },
        {
            Header: (
                <FilterTitle
                    title="Process"
                    filter={filter}
                    placeholder="Process name"
                    id="processName"
                    onClick={preventSort}
                />
            ),
            id: "processName",
            accessor: `${baseAccessor}.process.title`,
            minWidth: 100,
            Cell: row => <div className={styles.ellipsisRow}>{row.value}</div>,
            sortable: false,
        },
        {
            Header: (
                <FilterTitle
                    title="Step"
                    filter={filter}
                    placeholder="Task"
                    id="taskName"
                    onClick={preventSort}
                />
            ),
            id: "taskStep",
            accessor: `${baseAccessor}.title`,
            minWidth: 100,
            Cell: row => {
                const task = getValueFromPath(
                    `${baseAccessor}`,
                    row.row._original
                );

                if (nameRenderer) {
                    return nameRenderer(task);
                }

                return <TaskTitle task={task || null} />;
            },
            filterable: false,
            sortable: false,
        },
        {
            Header: "Step Details",
            id: "taskCategories",
            accessor: d => {
                const properties = getValueFromPath(
                    `${baseAccessor}.properties.edges`,
                    d
                );
                const findCategories = R.find(
                    R.where({
                        node: R.propEq("name", "categories"),
                    })
                );
                return getValueFromPath(
                    "node.value",
                    findCategories(properties)
                );
            },
            maxWidth: 100,
            Cell: row => <Categories>{row.value}</Categories>,
            className: `${styles.categoriesCell}`,
            sortable: false,
        },
        {
            Header: <UsersFilter filter={filter} />,
            id: "taskAssignedTo",
            Cell: assignSelect(permissionsData),
            width: 200,
            sortable: false,
        },
        {
            Header: (
                <div onClick={preventSort}>
                    <DatesRange onChange={onDatesChange} />
                </div>
            ),
            id: "created",
            accessor: `${baseAccessor}.created`,
            Cell: row => formatDatetimeString({ str: row.value }),
            width: 130,
            sortable: true,
        },
    ];
};
